import isEqual from 'lodash/isEqual';
import { getKioskOrderLink } from '@/utils/orders';

const ORDER_SYNC_INTERVAL_MS = 1000;

// Kiosk (self-service table) orders that skip the terminal: full coupon
// discount, skip-payment functionality or pay-at-restaurant coupons. The order
// is placed straight away and the screen waits for it to show up in the
// session's orders, then prints the receipt and moves to the success screen.
//
// Shared by PlaceOrder and ExtraDetails, which both place orders. The host
// component must provide `showBackdrop(name, timeout)` and
// `onKioskOrderFailed()`.
export default {
  data() {
    return {
      uniqueOrderNumber: null,
      kioskOrderIsFree: false,
      kioskOrderSyncIntervalId: null,
      isSyncingKioskOrders: false,
    };
  },
  computed: {
    placedOrder() {
      const { getOrderByUniqueOrderNumber } = this.$store.getters;
      return getOrderByUniqueOrderNumber(this.uniqueOrderNumber);
    },
  },
  watch: {
    placedOrder(newOrder, oldOrder) {
      this.handleKioskOrderUpdate(newOrder, oldOrder);
    },
  },
  beforeDestroy() {
    this.stopKioskOrderSync();
  },
  methods: {
    // Call from the place-order success handler once payment is being skipped
    // on a self-service table.
    waitForKioskOrder(order) {
      this.kioskOrderIsFree = order.total === 0;
      this.uniqueOrderNumber = order.unique_order_number;
      this.$store.dispatch('subscribeToPusherChannels');
      // Pusher may already be subscribed (no-op above) or miss the event, and
      // the backend does not always emit one for orders without a payment, so
      // poll the orders like the pay-in-person screen does.
      this.startKioskOrderSync();
    },
    handleKioskOrderUpdate(newOrder, oldOrder) {
      const isSelfService = this.$store.getters.tableTypeIsSelfService;
      if (!newOrder || !isSelfService || isEqual(newOrder, oldOrder)) {
        return;
      }

      const sendingToPOS = newOrder.sending_to_pos;
      const orderSentToPOS = newOrder.sent_to_pos;
      const orderFailed = newOrder.status === 'failed';
      const integratedToPOS = this.$store.getters.posIntegrationEnabled;

      if (orderFailed) {
        this.stopKioskOrderSync();
        this.$store.dispatch('printReceipt', {
          text: this.$t('kiosk.printer.orderFailed'),
          orderId: newOrder.order_id,
        });
        this.showBackdrop('error', 2000);
        this.onKioskOrderFailed();
      } else if ((sendingToPOS && orderSentToPOS) || !integratedToPOS) {
        // Without a POS integration there is nothing to wait for: the order
        // existing is the confirmation. Previously this branch required the
        // POS flags, so non-integrated kiosks never printed a receipt for a
        // fully discounted order and stayed on the spinner.
        this.stopKioskOrderSync();
        this.printKioskOrderReceipt(newOrder);
        this.$router.push({
          name: 'PayInPersonSuccess',
          query: {
            orderNumber: newOrder.unique_order_number,
          },
          params: {
            session: newOrder.session_token || this.$store.state.session,
            paymentUUID: 'null',
          },
        });
      } else if (sendingToPOS) {
        this.showBackdrop('loading');
      }
    },
    printKioskOrderReceipt(order) {
      const session = this.$store.state.session;
      const QRLink = getKioskOrderLink(session, order.unique_order_number, {
        force: true,
      });

      this.$store.dispatch('printReceipt', {
        text: this.kioskOrderIsFree ? this.$t('kiosk.printer.couponCharge') : '',
        orderId: order.order_id,
        additionalFields: Array.isArray(order.additional_fields)
          ? order.additional_fields
          : [],
        QRLink,
        items: order.items || [],
        discount: order.discount,
        discountCode: order.coupon,
      });
    },
    startKioskOrderSync() {
      this.stopKioskOrderSync();
      this.syncKioskOrders();
      this.kioskOrderSyncIntervalId = setInterval(() => {
        this.syncKioskOrders();
      }, ORDER_SYNC_INTERVAL_MS);
    },
    stopKioskOrderSync() {
      if (!this.kioskOrderSyncIntervalId) {
        return;
      }

      clearInterval(this.kioskOrderSyncIntervalId);
      this.kioskOrderSyncIntervalId = null;
    },
    async syncKioskOrders() {
      if (this.isSyncingKioskOrders) {
        return;
      }

      this.isSyncingKioskOrders = true;
      try {
        await this.$store.dispatch('fetchOrders');
      } catch (error) {
        console.warn('KIOSK_ORDER: fallback order sync failed', error);
      } finally {
        this.isSyncingKioskOrders = false;
      }
    },
  },
};
